import React, { Suspense, useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Input,
  Button,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Code,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Divider,
  Checkbox,
  HStack,
  Collapse,
  Link,
} from '@chakra-ui/react';
import { useAuth, SessionNotVerifiedError } from '../context/AuthContext';
import { useUIStore } from '../store/uiStore';
import CounselorLogin from '../counselor-v1/CounselorLogin';
import { isAuth0Configured } from '../auth/auth0Config';
import {
  getRememberedAccessGuid,
  setRememberedAccessGuid,
  clearRememberedAccessGuid,
} from '../utils/rememberedAccessGuid';
import { parseAccessLinkFromInput } from '../utils/accessLink';
import BrandLogo from '../brand/BrandLogo';
import {
  assignLocationIfAbsolute,
  resolvePostLoginPath,
  resolvePortalPublicUrl,
} from '../config/appSurfaceRoutes';
import lazyWithReload from '../utils/lazyWithReload';

// Lazy: this is the only Auth0 SDK entry point on the login screen, and it
// only renders when Auth0 is configured — keeps @auth0/auth0-react out of the
// entry chunk (RSW-609). lazyWithReload recovers stale chunks after deploys.
const CounselorSsoButton = lazyWithReload(() => import('../auth/CounselorSsoButton'));

// SSO bridge-failure notice copy, keyed by the `reason` the bridge navigates
// back with (derived from the API error code in Auth0SessionBridge).
const SSO_FAILED_COPY = {
  'no-account': 'We couldn’t find a staff account for that email. Sign in with the work email your organization has on file with RS Works, or ask your partner admin to add you.',
  'org-suspended': 'Your organization’s RS Works account is currently suspended. Please contact your partner admin or RS Works support.',
  error: 'We couldn’t complete your sign-in. Please try again. If this keeps happening, contact your partner admin.',
};

function resolveGuidLoginError(err) {
  if (err instanceof SessionNotVerifiedError) {
    return 'We could not verify your session in this browser. Try another browser, disable strict privacy extensions, or request a new link by email below.';
  }
  return err.response?.data?.message || 'Invalid access link. Please check your link and try again.';
}

export default function AppShell({ loginMode = 'all' }) {
  const {
    loginWithGuid,
    loginWithCredentials,
    requestJobseekerAccessLink,
    user,
    userType,
  } = useAuth();
  const { guid: routeGuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = useUIStore((state) => state.authTabIndex);
  const setActiveTab = useUIStore((state) => state.setAuthTabIndex);
  const authDrafts = useUIStore((state) => state.authDrafts);
  const setAuthDraft = useUIStore((state) => state.setAuthDraft);
  const clearAuthDrafts = useUIStore((state) => state.clearAuthDrafts);
  const guid = authDrafts.guid;
  const email = authDrafts.email;
  const password = authDrafts.password;
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberAccessOnDevice, setRememberAccessOnDevice] = useState(true);
  const [hasRememberedGuid, setHasRememberedGuid] = useState(() => Boolean(getRememberedAccessGuid()));
  const [jobseekerEmail, setJobseekerEmail] = useState('');
  const [linkRequestSent, setLinkRequestSent] = useState(false);
  const [showAdvancedAccess, setShowAdvancedAccess] = useState(false);
  const [accessLinkInput, setAccessLinkInput] = useState('');
  const resumeGuid = typeof location.state?.resumeGuid === 'string'
    ? location.state.resumeGuid.trim()
    : '';
  const resumeEmail = typeof location.state?.resumeEmail === 'string'
    ? location.state.resumeEmail.trim()
    : '';
  const sessionExpiredNotice = location.state?.sessionExpired
    ? {
      audience: location.state?.audience ?? null,
      resumeGuid: resumeGuid || null,
      resumeEmail: resumeEmail || null,
    }
    : null;
  const ssoFailedNotice = location.state?.ssoFailed
    ? {
      audience: location.state?.audience ?? null,
      reason: location.state?.reason ?? 'error',
    }
    : null;
  const canContinueInvite = Boolean(resumeGuid || hasRememberedGuid);
  const isDev = import.meta.env.DEV;
  const showJobseekerLogin = loginMode === 'all' || loginMode === 'jobseeker';
  const showCounselorLogin = loginMode === 'all' || loginMode === 'counselor';
  const isCounselorTab = loginMode === 'counselor' || (loginMode === 'all' && activeTab === 1);
  const isJobseekerSurface = loginMode === 'jobseeker';
  const staffPortalUrl = isJobseekerSurface ? resolvePortalPublicUrl() : '';
  const devCounselorEmail = import.meta.env.VITE_DEV_COUNSELOR_EMAIL || 'admin@rsworks.com';
  const devCounselorPassword = import.meta.env.VITE_DEV_COUNSELOR_PASSWORD || 'password123';
  const devJobseekerGuid = import.meta.env.VITE_DEV_JOBSEEKER_GUID || 'demo-jobseeker-guid';

  useEffect(() => {
    if (!routeGuid) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        await loginWithGuid(routeGuid);
        if (!cancelled) setHasRememberedGuid(true);
      } catch (err) {
        if (!cancelled) {
          setError(resolveGuidLoginError(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [routeGuid, loginWithGuid]);

  useEffect(() => {
    if (!resumeEmail) return;
    setJobseekerEmail((prev) => (prev.trim() ? prev : resumeEmail));
  }, [resumeEmail]);

  useEffect(() => {
    if (resumeGuid) setHasRememberedGuid(true);
  }, [resumeGuid]);

  useEffect(() => {
    if (!user || !userType || loading) return;
    const target = resolvePostLoginPath({
      userType,
      userRole: user?.role,
    });
    if (assignLocationIfAbsolute(target)) return;
    // Preserve referral query (partner/program, warm handoff) through access → workspace.
    const search = location.search || '';
    navigate(
      search ? { pathname: target, search } : target,
      { replace: true },
    );
  }, [user, userType, user?.role, loading, navigate, location.search]);

  const dismissSessionNotice = () => {
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: {} },
    );
  };

  const handleGuidLogin = async (accessGuid) => {
    setLoading(true);
    setError('');
    const code = (accessGuid || guid || '').trim();
    try {
      await loginWithGuid(code);
      if (rememberAccessOnDevice && code) {
        setRememberedAccessGuid(code);
        setHasRememberedGuid(true);
      }
    } catch (err) {
      setError(resolveGuidLoginError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleAccessLinkOpen = async () => {
    const parsed = parseAccessLinkFromInput(accessLinkInput);
    if (!parsed?.guid) {
      setError('Paste your full invite link from email, or use the email sign-in option above.');
      return;
    }
    // Route through /access/:guid so partner/program query survives login (RSW-377).
    navigate(`/access/${encodeURIComponent(parsed.guid)}${parsed.search || ''}`);
  };

  const handleRequestAccessLink = async (e) => {
    e.preventDefault();
    const normalizedEmail = jobseekerEmail.trim().toLowerCase();
    if (!normalizedEmail) return;

    setLoading(true);
    setError('');
    setLinkRequestSent(false);
    try {
      const result = await requestJobseekerAccessLink(normalizedEmail);
      setLinkRequestSent(true);
      setJobseekerEmail(normalizedEmail);
      setAuthDraft('email', normalizedEmail);
      if (result?.message) {
        /* message shown via linkRequestSent alert */
      }
    } catch (err) {
      setError(err.response?.data?.message || 'We could not send a link right now. Please try again shortly.');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueRemembered = () => {
    const saved = resumeGuid || getRememberedAccessGuid();
    if (!saved) return;
    navigate(`/access/${encodeURIComponent(saved)}`, { replace: false });
  };

  const handleForgetRemembered = () => {
    clearRememberedAccessGuid();
    setHasRememberedGuid(false);
  };

  const handleCounselorLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await loginWithCredentials(email, password);
      clearAuthDrafts();
      const target = resolvePostLoginPath({
        userType: 'COUNSELOR',
        userRole: result?.user?.role,
      });
      if (assignLocationIfAbsolute(target)) return;
      navigate(target);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  const handleDevAutofillCounselor = () => {
    setActiveTab(1);
    setAuthDraft('email', devCounselorEmail);
    setAuthDraft('password', devCounselorPassword);
    setError('');
  };

  const handleDevAutofillJobseeker = () => {
    setActiveTab(0);
    setAuthDraft('guid', devJobseekerGuid);
    setError('');
  };

  const handleTabChange = (nextTab) => {
    setError('');
    setActiveTab(nextTab);
  };

  const devPanelStyles = {
    alignSelf: 'stretch',
    w: '100%',
    borderWidth: '1px',
    borderRadius: 'md',
    borderColor: 'blue.200',
    bg: 'blue.50',
    px: 4,
    py: 3,
  };

  const jobseekerEmailForm = (
    <VStack spacing={4} as="form" onSubmit={handleRequestAccessLink}>
      {!linkRequestSent ? (
        <Text color="gray.600" fontSize="sm" textAlign="center">
          {isJobseekerSurface
            ? 'Enter the email your counselor used to set up your account. We will email you a secure link to open your career workspace.'
            : 'Enter your email to receive a secure link to your career workspace.'}
        </Text>
      ) : null}
      {linkRequestSent ? (
        <Alert status="success" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            If your email is on file with RS Works, we sent a secure sign-in link. Check your inbox and spam folder.
            Your access begins when you first sign in.
          </Text>
        </Alert>
      ) : null}
      {!linkRequestSent ? (
        <>
          <FormControl isRequired>
            <FormLabel>Email address</FormLabel>
            <Input
              type="email"
              value={jobseekerEmail}
              onChange={(e) => setJobseekerEmail(e.target.value)}
              placeholder="you@example.com"
              size="lg"
              autoComplete="email"
            />
          </FormControl>
          <Button
            type="submit"
            colorScheme="blue"
            size="lg"
            width="full"
            isLoading={loading}
            isDisabled={!jobseekerEmail.trim()}
          >
            Send secure link
          </Button>
          <Text color="brand.text.muted" fontSize="xs" textAlign="center">
            Already have an invite link from your counselor? Open it from your email, or use the option below.
          </Text>
        </>
      ) : null}
    </VStack>
  );

  const jobseekerAdvancedAccess = (
    <Box alignSelf="stretch" w="full">
      <Button
        variant="link"
        size="sm"
        color="gray.600"
        onClick={() => setShowAdvancedAccess((prev) => !prev)}
      >
        {showAdvancedAccess ? 'Hide invite link option' : 'Have an invite link? Paste it here'}
      </Button>
      <Collapse in={showAdvancedAccess} animateOpacity>
        <VStack spacing={3} mt={3} align="stretch">
          <FormControl>
            <FormLabel fontSize="sm">Invite link or access code</FormLabel>
            <Input
              value={accessLinkInput}
              onChange={(e) => setAccessLinkInput(e.target.value)}
              placeholder="https://my.rsworks.app/access/… or access code"
              size="md"
            />
          </FormControl>
          <Button
            size="md"
            variant="outline"
            width="full"
            onClick={handleAccessLinkOpen}
            isLoading={loading}
            isDisabled={!accessLinkInput.trim()}
          >
            Open from invite link
          </Button>
        </VStack>
      </Collapse>
    </Box>
  );

  const jobseekerLegacyAccessForm = (
    <VStack spacing={4}>
      <Text color="gray.600" fontSize="sm" textAlign="center">
        Enter the secure access code from your counselor to open your guided resume workflow.
      </Text>
      {isDev && (
        <Box {...devPanelStyles}>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color="blue.800"
            textTransform="uppercase"
            letterSpacing="wider"
            mb={3}
          >
            Development only
          </Text>
          <VStack align="stretch" spacing={3}>
            <Box>
              <Text fontSize="xs" color="gray.600" mb={1}>
                Demo access code (from seed data)
              </Text>
              <Code
                display="block"
                fontSize="xs"
                px={2}
                py={2}
                borderRadius="md"
                whiteSpace="normal"
                wordBreak="break-all"
              >
                {devJobseekerGuid}
              </Code>
            </Box>
            <Button
              size="sm"
              colorScheme="blue"
              w="full"
              onClick={handleDevAutofillJobseeker}
              isDisabled={loading}
            >
              Fill access code
            </Button>
          </VStack>
        </Box>
      )}
      <FormControl>
        <FormLabel>Access Code</FormLabel>
        <Input
          value={guid}
          onChange={(e) => setAuthDraft('guid', e.target.value)}
          placeholder="Enter your access code"
          size="lg"
        />
      </FormControl>
      <Text color="brand.text.muted" fontSize="xs" textAlign="left" w="full">
        Your secure session may time out after inactivity, so save changes regularly once signed in.
      </Text>
      <Checkbox
        isChecked={rememberAccessOnDevice}
        onChange={(e) => setRememberAccessOnDevice(e.target.checked)}
        size="sm"
        colorScheme="blue"
      >
        <Text as="span" fontSize="xs" color="gray.600">
          Keep a shortcut on this browser (not for shared computers). We store the access code
          locally—not your session. Uncheck if this is a shared device.
        </Text>
      </Checkbox>
      <Button
        colorScheme="blue"
        size="lg"
        width="full"
        onClick={() => handleGuidLogin()}
        isLoading={loading}
        isDisabled={!guid.trim()}
      >
        Open Resume Workspace
      </Button>
    </VStack>
  );

  const jobseekerAccessForm = isJobseekerSurface ? (
    <VStack spacing={4} align="stretch">
      {jobseekerEmailForm}
      {jobseekerAdvancedAccess}
      {isDev ? (
        <Box {...devPanelStyles} mt={2}>
          <Text fontSize="xs" color="gray.600" mb={2}>Dev quick entry</Text>
          <Button
            size="sm"
            colorScheme="blue"
            w="full"
            onClick={() => navigate(`/access/${encodeURIComponent(devJobseekerGuid)}`)}
          >
            Open demo jobseeker link
          </Button>
        </Box>
      ) : null}
    </VStack>
  ) : jobseekerLegacyAccessForm;

  const counselorAccessForm = (
    <CounselorLogin
      email={email}
      password={password}
      onEmailChange={(value) => setAuthDraft('email', value)}
      onPasswordChange={(value) => setAuthDraft('password', value)}
      onSubmit={handleCounselorLogin}
      loading={loading}
      error={error}
      isDev={isDev}
      devEmail={devCounselorEmail}
      devPassword={devCounselorPassword}
      onDevAutofill={handleDevAutofillCounselor}
      ssoSlot={showCounselorLogin && isAuth0Configured() ? (
        <Suspense fallback={null}>
          <CounselorSsoButton disabled={loading} />
        </Suspense>
      ) : null}
    />
  );

  if (routeGuid && loading && !error) {
    return (
      <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <Heading size="md" color="blue.700">
            Signing you in…
          </Heading>
          <Text color="gray.600" fontSize="sm">
            One moment while we verify your session and open your resume workspace.
          </Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="brand.bg.page" display="flex" alignItems="center" justifyContent="center">
      <Container maxW={isCounselorTab ? '5xl' : 'md'} py={12}>
        <VStack spacing={8} align="stretch">
          {!isCounselorTab && (
            <Box textAlign="center">
              <BrandLogo
                theme="light"
                className="access-shell__logo"
                style={{ height: '40px', width: 'auto', margin: '0 auto 12px', display: 'block' }}
              />
              <Text color="brand.text.secondary" fontSize="lg">
                Resume Builder &amp; Career Exploration Tools
              </Text>
              <Text color="brand.text.secondary" fontSize="sm" mt={1}>
                Career advancement platform for workforce participants
              </Text>
            </Box>
          )}

          <Box
            bg={isCounselorTab ? 'transparent' : 'white'}
            p={isCounselorTab ? 0 : 8}
            borderRadius="xl"
            boxShadow={isCounselorTab ? 'none' : 'lg'}
          >
            {sessionExpiredNotice && (
              <Alert status="warning" borderRadius="md" mb={4} data-testid="session-expired-notice">
                <AlertIcon />
                <Box flex="1">
                  {sessionExpiredNotice.audience === 'COUNSELOR' ? (
                    <Text fontSize="sm">
                      Your staff session ended. Please sign in again with your work email.
                    </Text>
                  ) : sessionExpiredNotice.audience === 'JOBSEEKER' ? (
                    <VStack align="stretch" spacing={3}>
                      <Text fontSize="sm">
                        Your session timed out for security. You can continue with the invite on this
                        device, or enter your email below and we will send a fresh secure link.
                      </Text>
                      {canContinueInvite ? (
                        <HStack spacing={3} flexWrap="wrap">
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={handleContinueRemembered}
                            isDisabled={loading}
                            data-testid="session-expired-continue"
                          >
                            Continue with this invite
                          </Button>
                          <Button size="xs" variant="link" onClick={dismissSessionNotice}>
                            Dismiss
                          </Button>
                        </HStack>
                      ) : (
                        <Button size="xs" variant="link" alignSelf="flex-start" onClick={dismissSessionNotice}>
                          Dismiss
                        </Button>
                      )}
                    </VStack>
                  ) : (
                    <Text fontSize="sm">
                      Your session is no longer active. Sign in as a counselor or request a jobseeker secure link
                      to continue.
                    </Text>
                  )}
                  {sessionExpiredNotice.audience !== 'JOBSEEKER' ? (
                    <Button size="xs" variant="link" mt={2} onClick={dismissSessionNotice}>
                      Dismiss
                    </Button>
                  ) : null}
                </Box>
              </Alert>
            )}

            {ssoFailedNotice && (
              <Alert
                status="error"
                borderRadius="md"
                mb={4}
                data-testid="sso-failed-notice"
                data-reason={ssoFailedNotice.reason}
              >
                <AlertIcon />
                <Box flex="1">
                  <Text fontSize="sm">
                    {SSO_FAILED_COPY[ssoFailedNotice.reason] ?? SSO_FAILED_COPY.error}
                  </Text>
                  <Button size="xs" variant="link" mt={2} onClick={dismissSessionNotice}>
                    Dismiss
                  </Button>
                </Box>
              </Alert>
            )}

            {error && !isCounselorTab && (
              <Alert status="error" borderRadius="md" mb={4}>
                <AlertIcon />
                {error}
              </Alert>
            )}

            {!routeGuid && !user && canContinueInvite && !sessionExpiredNotice && isJobseekerSurface && (
              <Alert status="info" borderRadius="md" mb={4}>
                <AlertIcon />
                <Box flex="1">
                  <Text fontSize="sm" fontWeight="medium" mb={1}>
                    Saved invite on this device
                  </Text>
                  <Text fontSize="sm" color="gray.600" mb={3}>
                    Reopen your workspace using the invite you opened in this browser. Do not use this on shared
                    computers.
                  </Text>
                  <HStack spacing={3} flexWrap="wrap">
                    <Button size="sm" colorScheme="blue" onClick={handleContinueRemembered} isDisabled={loading}>
                      Continue with saved invite
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleForgetRemembered}>
                      Forget this device
                    </Button>
                  </HStack>
                </Box>
              </Alert>
            )}

            {!routeGuid && !user && hasRememberedGuid && !sessionExpiredNotice && !isJobseekerSurface && (
              <Alert status="info" borderRadius="md" mb={4}>
                <AlertIcon />
                <Box flex="1">
                  <Text fontSize="sm" fontWeight="medium" mb={1}>
                    Saved access on this device
                  </Text>
                  <Text fontSize="sm" color="gray.600" mb={3}>
                    Open your workspace with the access code you saved in this browser. Do not use this on shared
                    computers.
                  </Text>
                  <HStack spacing={3} flexWrap="wrap">
                    <Button size="sm" colorScheme="blue" onClick={handleContinueRemembered} isDisabled={loading}>
                      Continue with saved access
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleForgetRemembered}>
                      Forget this device
                    </Button>
                  </HStack>
                </Box>
              </Alert>
            )}

            {loginMode === 'all' ? (
              <Tabs
                index={activeTab}
                onChange={handleTabChange}
                isFitted
              >
                <TabList mb={6}>
                  <Tab>Job Seeker Access</Tab>
                  <Tab>Counselor Login</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel p={0}>{jobseekerAccessForm}</TabPanel>
                  <TabPanel p={0}>{counselorAccessForm}</TabPanel>
                </TabPanels>
              </Tabs>
            ) : (
              <>
                {showJobseekerLogin ? jobseekerAccessForm : null}
                {showCounselorLogin ? counselorAccessForm : null}
              </>
            )}

            <Divider my={4} />
            {staffPortalUrl ? (
              <Text color="brand.text.secondary" fontSize="sm" textAlign="center" mb={3} data-testid="staff-portal-link">
                Counselor or staff?{' '}
                <Link href={staffPortalUrl} color="blue.600" fontWeight="medium">
                  Sign in at the staff portal
                </Link>
              </Text>
            ) : null}
            <Text color="brand.text.muted" fontSize="xs" textAlign="center">
              &copy; {new Date().getFullYear()} RS Works. All rights reserved.
            </Text>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
